import clsx from "clsx";
import {useEffect, useMemo} from "react";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useHistory, useParams} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {SubmitButton, ToggleSwitch} from "components/Button";
import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {CircleLoader} from "components/Loader";
import {useHospitals} from "hooks/SuperAdmin/Hospital";
import {
	useAddCustomPaymentMethod,
	useCustomPaymentMethodDetail,
	useEditCustomPaymentMethod,
} from "hooks/SuperAdmin/PaymentMethod";
import {useToast} from "hooks/useToast";
import {convertErrorMessageFormat} from "utils/converter";

const FormSchemaValidation = Yup.object().shape({
	name: Yup.string().required("Nama wajib diisi"),
	hospitalIds: Yup.array().of(Yup.mixed().required()).min(1, "Rumah sakit wajib diisi"),
	active: Yup.boolean().required("Status aktif wajib diisi"),
});

const FormInitialValues = {
	name: "",
	hospitalIds: [],
	active: true,
};

export function CustomPaymentSettingActionPage() {
	const {action, id} = useParams();
	const history = useHistory();
	const {showToast} = useToast();

	const {hospitals, isLoading: isLoadingHospital} = useHospitals(true, 100);
	const hospitalOptions = useMemo(
		() => (hospitals?.data?.rows || []).map(item => ({label: item.name, value: item.id})),
		[hospitals],
	);

	const isEdit = action === "edit";
	const {data: detailData, isLoading: isLoadingDetail} = useCustomPaymentMethodDetail(isEdit ? id : null);
	const {mutate: createPaymentMethod, isLoading: isCreating} = useAddCustomPaymentMethod();
	const {mutate: editPaymentMethod, isLoading: isEditing} = useEditCustomPaymentMethod();

	const {
		values,
		errors,
		touched,
		handleBlur,
		handleChange,
		setFieldValue,
		setFieldTouched,
		setFieldError,
		handleSubmit,
	} = useFormik({
		initialValues: FormInitialValues,
		validationSchema: FormSchemaValidation,
		onSubmit: formValues => {
			const payload = {
				name: formValues.name,
				hospitalIds: formValues.hospitalIds,
				active: !!formValues.active,
			};

			if (isEdit) {
				editPaymentMethod(
					{id, payload},
					{
						onSuccess: () => {
							showToast("success", "Data berhasil diperbarui", 3000);
							history.push("/custom-payment-setting");
						},
						onError: res => {
							showToast(
								"error",
								convertErrorMessageFormat(res.response.status, res.response.data.message),
								null,
							);
						},
					},
				);

				return;
			}

			createPaymentMethod(payload, {
				onSuccess: () => {
					showToast("success", "Data berhasil ditambahkan", 3000);
					history.push("/custom-payment-setting");
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			});
		},
	});

	useEffect(() => {
		if (isEdit && detailData?.data) {
			setFieldValue("name", detailData.data.name || "");
			setFieldValue("hospitalIds", (detailData.data.hospitals || []).map(item => item.id).filter(Boolean));
			setFieldValue("active", !!detailData.data.active);

			setFieldTouched("name", false, false);
			setFieldTouched("hospitalIds", false, false);
			setFieldTouched("active", false, false);

			setFieldError("name", undefined);
			setFieldError("hospitalIds", undefined);
			setFieldError("active", undefined);
		}
	}, [detailData, isEdit, setFieldError, setFieldTouched, setFieldValue]);

	const selectedHospitalOptions = useMemo(
		() => hospitalOptions.filter(option => values.hospitalIds.map(String).includes(String(option.value))),
		[hospitalOptions, values.hospitalIds],
	);

	const isLoading = isCreating || isEditing;

	if (isEdit && isLoadingDetail) {
		return (
			<DashboardLayout>
				<div className="box-white setting-content h-100 no-border p24">
					<CircleLoader />
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<form onSubmit={handleSubmit}>
				<div className="box-white setting-content h-100 no-border p24">
					<div className="d-flex justify-content-between flex-wrap align-items-center mb-4">
						<div className="ttl-20">{isEdit ? "Edit" : "Tambah"} Custom Payment Setting</div>
					</div>

					<hr />

					<div className="text-bold mb-4">
						<a
							className="text-dark"
							style={{cursor: "pointer"}}
							onClick={() => history.push("/custom-payment-setting")}>
							<span className="icon-ico-back me-2" />
						</a>
						Daftar Custom Payment Setting
					</div>

					<div className="row gx-2 mb-5">
						<div className="col-sm-12 col-lg-6 col-xs-12">
							<div className="setting-item">
								<label className="setting-label required">Nama</label>
								<input
									className={clsx(
										"form-control",
										errors?.name && touched?.name && "form-control-error",
									)}
									disabled={isLoading}
									name="name"
									placeholder="Masukkan nama"
									type="text"
									value={values.name}
									onBlur={handleBlur}
									onChange={handleChange}
								/>
								{errors.name && touched?.name && (
									<p className="form-error-item-message">{errors.name}</p>
								)}
							</div>
						</div>

						<div className="col-sm-12 col-lg-6 col-xs-12">
							<div className="setting-item">
								<label className="setting-label required">Rumah Sakit</label>
								<DropdownAsync
									defaultOptions={hospitalOptions}
									disabled={isLoading || isLoadingHospital}
									fetchOptions={(inputValue, cb) => {
										const filtered = hospitalOptions.filter(option =>
											option.label.toLowerCase().includes((inputValue || "").toLowerCase()),
										);

										cb(filtered);
									}}
									isError={errors?.hospitalIds && touched?.hospitalIds}
									isMulti={true}
									label={isLoadingHospital ? "Loading..." : "Pilih rumah sakit"}
									value={selectedHospitalOptions}
									onChange={selectedValues => {
										const selectedOptions = Array.isArray(selectedValues)
											? selectedValues
											: selectedValues
											? [selectedValues]
											: [];
										const selectedIds = selectedOptions
											.map(option => option?.value)
											.filter(value => value !== null && value !== undefined);

										setFieldValue("hospitalIds", selectedIds, true);
										setFieldTouched("hospitalIds", true, false);

										if (selectedIds.length > 0) {
											setFieldError("hospitalIds", undefined);
										}
									}}
								/>
								{errors.hospitalIds && touched?.hospitalIds && (
									<p className="form-error-item-message">{errors.hospitalIds}</p>
								)}
							</div>
						</div>

						<div className="col-sm-12 col-lg-6 col-xs-12">
							<div className="setting-item">
								<label className="setting-label required d-block">Active</label>
								<ToggleSwitch
									isActive={!!values.active}
									onChange={({status}) => {
										setFieldValue("active", !!status);
										setFieldTouched("active", true);
									}}
								/>
							</div>
						</div>
					</div>

					<div className="text-center my-3">
						<SubmitButton
							className="btn btn-orange d-inline-block mw-250"
							disabled={!FormSchemaValidation.isValidSync(values)}
							loading={isLoading}
							text={isEdit ? "Simpan Perubahan" : "Simpan"}
						/>
					</div>
				</div>
			</form>
		</DashboardLayout>
	);
}
